import React from "react";
import { Link } from "react-router-dom";
import {
  AcademicCapIcon,
  BookOpenIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import logo from "../assets/logo.jpg";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "Syllabus", href: "/syllabus" },
    { name: "Reading Material", href: "/reading-material" },
    { name: "MCQs", href: "/mcqs" },
  ];

  const companyLinks = [
    { name: "About Us", href: "/about" },
    { name: "Contact", href: "/contact" },
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
  ];

  const features = [
    { name: "Comprehensive Syllabus", icon: DocumentTextIcon },
    { name: "Quality Reading Material", icon: BookOpenIcon },
    { name: "Interactive MCQs", icon: QuestionMarkCircleIcon },
  ];

  return (
    <footer className="bg-white border-t-2 border-gray-200">
      <div className="container-classic section-spacing">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="col-span-1 lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <img src={logo} alt="WebDoko Logo" className="w-10 h-10" />
              <span className="text-2xl font-serif font-semibold text-gray-900">
                WebDoko
              </span>
            </div>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Your comprehensive academic hub connecting universities and
              academia. Access quality educational resources, practice
              materials, and interactive learning tools.
            </p>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-sm text-gray-700">
                <EnvelopeIcon className="w-4 h-4 text-gray-500" />
                <span>
                  {import.meta.env.VITE_CONTACT_EMAIL || "contact@webdoko.com"}
                </span>
              </div>
              <div className="flex items-center space-x-3 text-sm text-gray-700">
                <PhoneIcon className="w-4 h-4 text-gray-500" />
                <span>+977 9849826321</span>
              </div>
              <div className="flex items-center space-x-3 text-sm text-gray-700">
                <MapPinIcon className="w-4 h-4 text-gray-500" />
                <span>Global Academic Network</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-serif font-semibold mb-6 text-gray-900 uppercase tracking-wide">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-600 hover:text-gray-900 transition-colors duration-200 text-sm font-medium"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-lg font-serif font-semibold mb-6 text-gray-900 uppercase tracking-wide">
              Company
            </h3>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-600 hover:text-gray-900 transition-colors duration-200 text-sm font-medium"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-lg font-serif font-semibold mb-6 text-gray-900 uppercase tracking-wide">
              Features
            </h3>
            <ul className="space-y-4">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <li
                    key={feature.name}
                    className="flex items-center space-x-3"
                  >
                    <Icon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    <span className="text-gray-600 text-sm font-medium">
                      {feature.name}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Coming Soon Section */}
        <div className="content-divider">
          <div className="text-center py-12">
            <h4 className="text-xl font-serif font-semibold mb-4 text-gray-900 uppercase tracking-wide">
              Coming Soon
            </h4>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto leading-relaxed">
              Exciting new features are on the way to enhance your academic
              journey!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="badge-primary">Community Activities</span>
              <span className="badge-secondary">Online Courses</span>
              <span className="badge-accent">Live Sessions</span>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="section-divider">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 py-8">
            <div className="text-gray-600 text-sm font-medium">
              © {currentYear} WebDoko. All rights reserved. Built for academic
              excellence.
            </div>
            <div className="flex space-x-8 text-sm">
              <Link
                to="/privacy"
                className="text-gray-600 hover:text-gray-900 transition-colors duration-200 font-medium"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-gray-600 hover:text-gray-900 transition-colors duration-200 font-medium"
              >
                Terms of Service
              </Link>
              <Link
                to="/cookies"
                className="text-gray-600 hover:text-gray-900 transition-colors duration-200 font-medium"
              >
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
